import { useAppSelector } from "@/app/hooks";
import { SurveySection } from "@/features/survey/types/survey.types";
import { useWindowSize } from "@/shared/hooks/useWindowSize";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import InteractiveSurveyMenuBar from "./InteractiveSurveyMenuBar";

interface SectionPDF {
  mediaUrl?: string;
  url?: string;
  cloudfrontUrl?: string;
  pdfUrl?: string;
}

export interface MenuItem {
  path: string;
  label: string;
  isCustom?: boolean;
  section?: SurveySection;
  order?: number;
  name?: string;
}

export const SECTION_ROUTE_MAP: Record<string, { route: string; label: string }> = {
  cover: { route: "/cover", label: "Overview" },
  "market map": { route: "/marketmap", label: "Market Map" },
  "property comparison": { route: "/comparison", label: "Property Comparison" },
  properties: { route: "/property/", label: "Properties" },
  "tour schedule": { route: "/tour", label: "Tour Schedule" },
  "lease comps": { route: "/leasecomps", label: "Lease Comps" },
  "additional section": { route: "/additional", label: "Additional Section" },
  more: { route: "/more", label: "More" },
} as const;

export const DEFAULT_MENU_ITEMS: ReadonlyArray<
  Omit<MenuItem, "isCustom" | "section" | "order" | "name">
> = [
  { path: "/cover", label: "Overview" },
  { path: "/marketmap", label: "Market Map" },
  { path: "/comparison", label: "Property Comparison" },
  { path: "/property/", label: "Properties" },
] as const;

/**
 * Built-in sections whose nav label may be overridden by the author-defined
 * `displayName`. Other sections keep their fixed labels (e.g. the Properties
 * tab's label drives its dropdown behaviour).
 */
const DISPLAY_NAME_SECTIONS: ReadonlySet<string> = new Set([
  "market map",
  "property comparison",
]);

/**
 * Builds the section-based nav menu items for a given survey. Pure function so
 * it can be reused outside of the single-survey layout (e.g. group cover sub-tabs).
 */
export const buildSurveyMenuItems = (
  surveyId: string,
  sections: SurveySection[] | undefined,
): MenuItem[] => {
  if (!sections) {
    return DEFAULT_MENU_ITEMS.map((item) => ({
      ...item,
      path: `/interactive/${surveyId}${item.path}`,
    }));
  }

  const visibleSections = sections
    .filter((section) => !section.isHidden)
    .sort((a, b) => a.order - b.order);

  if (visibleSections.length === 0) return [];

  return visibleSections
    .map((section): MenuItem | null => {
      const sectionKey = section.name.toLowerCase();
      const sectionInfo = SECTION_ROUTE_MAP[sectionKey];
      const customLabel = DISPLAY_NAME_SECTIONS.has(sectionKey)
        ? section.displayName?.trim()
        : undefined;

      // Handle known sections (built-in pages)
      if (sectionInfo) {
        return {
          path: `/interactive/${surveyId}${sectionInfo.route}`,
          label: customLabel || sectionInfo.label,
          name: section.name,
          isCustom: false,
        };
      }

      // Handle custom sections with PDFs
      if (section.isCustom && section.hasPDF && section.pdfs?.length) {
        const firstPdf = section.pdfs[0] as SectionPDF;
        const pdfUrl =
          firstPdf.mediaUrl ||
          firstPdf.url ||
          firstPdf.cloudfrontUrl ||
          firstPdf.pdfUrl ||
          "";

        return {
          path: pdfUrl,
          label: section.name,
          name: section.name,
          isCustom: true,
          section,
        };
      }

      return null;
    })
    .filter((item): item is MenuItem => item !== null);
};

const MenuWrapper = ({ hideBranding = false }: { hideBranding?: boolean }) => {
  const { surveyId } = useParams<{
    surveyId: string;
    propertyId: string;
  }>();
  const { currentSurvey } = useAppSelector((state) => state.interactiveSurvey);
  const [visibleMenuItems, setVisibleMenuItems] = useState<MenuItem[]>([]);
  const [width] = useWindowSize();

  useEffect(() => {
    if (!surveyId) return;
    setVisibleMenuItems(buildSurveyMenuItems(surveyId, currentSurvey?.sections));
  }, [surveyId, currentSurvey?.sections]);

  if (!surveyId) return null;

  return (
    <InteractiveSurveyMenuBar
      menuItems={visibleMenuItems}
      hideBranding={hideBranding}
      key={width}
    />
  );
};

export default MenuWrapper;

